import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import ShareModal from "@/components/base/ShareModal";
import EmptyState from "@/components/base/EmptyState";
import { works } from "@/mocks/works";
import { useFavorites } from "@/hooks/useFavorites";
import { workTypeLabel } from "@/utils/format";
import type { Work } from "@/types";

export default function WorkDetail() {
  const { id } = useParams();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  const allWorks = works as Work[];
  const work = allWorks.find((item) => item.id === id);

  const related = useMemo(() => {
    if (!work) return [];
    return allWorks
      .filter((item) => item.id !== work.id && item.type === work.type)
      .sort((a, b) => b.rating - a.rating)
      .slice(0, 4);
  }, [allWorks, work]);

  if (!work) {
    return (
      <div className="pt-16 md:pt-[72px] px-5 md:px-10 py-20">
        <EmptyState
          icon="ri-file-unknow-line"
          title="未找到该作品"
          description="你访问的作品可能已被移除或链接有误，返回作品列表继续浏览吧。"
          actionLabel="返回作品列表"
          onAction={() => window.history.back()}
        />
        <div className="flex justify-center mt-6">
          <Link
            to="/works"
            className="inline-flex items-center gap-2 text-sm text-primary-700 hover:text-accent-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            返回影视作品列表
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(work.id);

  const handleFavorite = () => {
    const nowFavorited = toggleFavorite(work.id);
    setToast(nowFavorited ? "已加入收藏" : "已取消收藏");
    window.setTimeout(() => setToast(""), 2000);
  };

  const handleShare = () => {
    setShareOpen(true);
  };

  return (
    <div className="pt-16 md:pt-[72px]">
      <PageMeta
        title={`《${work.title}》详情 - 佐山爱影视作品`}
        description={`《${work.title}》是佐山爱主演的${workTypeLabel(work.type)}作品，${work.year} 年上映，类型为${work.genre}，评分 ${work.rating}。查看作品简介、演职人员与关联推荐。`}
        keywords={`${work.title},佐山爱${workTypeLabel(work.type)},${work.genre},佐山爱影视作品,作品详情`}
      />

      {/* 顶部封面横幅 */}
      <section className="relative h-[280px] md:h-[380px] overflow-hidden bg-primary-950">
        <img
          src={work.cover}
          alt={`${work.title} 封面`}
          className="w-full h-full object-cover object-top opacity-45 blur-[2px] scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/40" />
        <div className="absolute inset-0 flex items-end px-5 md:px-10 pb-8 md:pb-10">
          <div className="w-full">
            <div className="mb-4 [&_a]:text-white/70 [&_a:hover]:text-accent-400 [&_span]:text-white/70 [&_span.font-medium]:text-white">
              <Breadcrumb
                items={[
                  { label: "首页", to: "/" },
                  { label: "影视作品", to: "/works" },
                  { label: work.title },
                ]}
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-accent-500 text-primary-950">
                {workTypeLabel(work.type)}
              </span>
              <span className="px-2.5 py-1 rounded-md text-xs text-white bg-white/15 backdrop-blur-sm">
                {work.year}
              </span>
              <span className="px-2.5 py-1 rounded-md text-xs text-white bg-white/15 backdrop-blur-sm">
                {work.genre}
              </span>
              {work.episodes && (
                <span className="px-2.5 py-1 rounded-md text-xs text-white bg-white/15 backdrop-blur-sm">
                  全 {work.episodes} 集
                </span>
              )}
            </div>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-white">
              《{work.title}》
            </h1>
          </div>
        </div>
      </section>

      {/* 主体内容 */}
      <section className="px-5 md:px-10 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] gap-8 lg:gap-12 items-start">
          {/* 左：海报与操作 */}
          <div className="lg:sticky lg:top-28">
            <div className="w-full max-w-[320px] mx-auto lg:mx-0 rounded-lg overflow-hidden border border-background-200/70 bg-background-100">
              <img
                src={work.cover}
                alt={`${work.title} 海报`}
                title={`${work.title} - 佐山爱影视作品`}
                className="w-full aspect-[3/4] object-cover object-top"
              />
            </div>

            <div className="flex items-center gap-3 mt-5 max-w-[320px] mx-auto lg:mx-0">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  favorited
                    ? "bg-accent-500 text-primary-950 hover:bg-accent-600"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {favorited ? "已收藏" : "收藏作品"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                aria-label="分享作品"
                className="flex items-center justify-center w-12 h-12 rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-share-forward-line text-lg" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4 max-w-[320px] mx-auto lg:mx-0">
              <div className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                <span className="block text-xs text-foreground-400 mb-1">观众评分</span>
                <span className="font-heading font-bold text-xl text-primary-600 flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500 text-base" />
                  {work.rating.toFixed(1)}
                </span>
              </div>
              <div className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                <span className="block text-xs text-foreground-400 mb-1">上映年份</span>
                <span className="font-heading font-bold text-xl text-foreground-950">
                  {work.year}
                </span>
              </div>
            </div>
          </div>

          {/* 右：资料与简介 */}
          <div>
            <h2 className="font-heading font-semibold text-xl text-foreground-950 mb-4 pb-3 border-b border-background-200">
              作品资料
            </h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4 mb-8">
              <div className="flex flex-col gap-1">
                <dt className="text-xs text-foreground-400">作品类型</dt>
                <dd className="text-sm text-foreground-800">{workTypeLabel(work.type)}</dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-xs text-foreground-400">题材分类</dt>
                <dd className="text-sm text-foreground-800">{work.genre}</dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-xs text-foreground-400">制作公司</dt>
                <dd className="text-sm text-foreground-800">{work.company}</dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-xs text-foreground-400">导演</dt>
                <dd className="text-sm text-foreground-800">{work.director}</dd>
              </div>
              <div className="flex flex-col gap-1 sm:col-span-2">
                <dt className="text-xs text-foreground-400">主要演员</dt>
                <dd className="flex flex-wrap gap-2 mt-1">
                  {work.cast.map((member) => (
                    <span
                      key={member}
                      className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900"
                    >
                      {member}
                    </span>
                  ))}
                </dd>
              </div>
            </dl>

            <h2 className="font-heading font-semibold text-xl text-foreground-950 mb-4 pb-3 border-b border-background-200">
              作品简介
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-loose mb-8">
              {work.synopsis}
            </p>

            <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70 mb-8">
              <p className="text-xs text-foreground-400 leading-relaxed">
                <i className="ri-information-line mr-1.5" />
                本站作品资料、公司、导演与演员均为演示用的虚构信息，仅用于展示页面效果。
                收藏状态保存在你的浏览器本地，刷新后依然保留。
              </p>
            </div>

            <Link
              to="/works"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-accent-600 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              返回影视作品列表
            </Link>
          </div>
        </div>

        {/* 关联推荐 */}
        {related.length > 0 && (
          <div className="mt-14 md:mt-20">
            <div className="flex items-end justify-between gap-4 mb-8">
              <div>
                <span className="text-xs font-medium tracking-[0.2em] uppercase text-accent-600">
                  You May Also Like
                </span>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mt-2">
                  关联作品推荐
                </h2>
              </div>
              <Link
                to="/works"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-accent-600 transition-colors whitespace-nowrap"
              >
                更多作品
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {related.map((item) => (
                <WorkCard key={item.id} work={item} />
              ))}
            </div>
          </div>
        )}
      </section>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-md bg-primary-950 text-background-50 text-sm">
          {toast}
        </div>
      )}

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={`《${work.title}》 - 佐山爱影视作品`}
      />
    </div>
  );
}